import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { publishCounter } from '../utils/metrics';
import { logger } from '../utils/logger';
import type { LoggedInUserInfo } from '../types/auth/auth.types';

interface UsePageViewTrackingProps {
  user: LoggedInUserInfo | null;
  metricsReady: boolean;
}

/**
 * Custom hook to track page views and navigation patterns
 *
 * This hook:
 * 1. Publishes "PageView" counter on every route change
 * 2. Logs navigation path for user journey analysis
 * 3. Tracks time spent on each page
 *
 * @param user - The authenticated user information
 * @param metricsReady - Whether metrics system is initialized
 */
export const usePageViewTracking = ({ user, metricsReady }: UsePageViewTrackingProps): void => {
  const location = useLocation();
  const previousPathRef = useRef<string | null>(null);
  const pageStartTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!user || !metricsReady) {
      return;
    }

    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    // Calculate time on previous page
    if (previousPath) {
      const timeOnPage = Date.now() - pageStartTimeRef.current;

      logger.info('Navigation: Page view ended', {
        logType: 'navigation',
        page: previousPath,
        timeOnPageMs: timeOnPage,
        nextPage: currentPath,
      });
    }

    // Reset timer for new page
    pageStartTimeRef.current = Date.now();

    // Publish PageView metric
    publishCounter('PageView', 1, undefined, {
      userAlias: user.userAlias,
      pageName: currentPath,
    });

    logger.info('Navigation: Page view started', {
      logType: 'navigation',
      page: currentPath,
      previousPage: previousPath || 'none',
    });

    // Update previous path
    previousPathRef.current = currentPath;
  }, [location.pathname, user, metricsReady]);
};
